import { ComponentConfig, FieldLabel } from '@measured/puck';
import { useSearchParams } from 'react-router-dom';
import {
  MarginProps,
  marginFields,
  marginProps,
} from '../../../page-builder/puck/reusable-props/NewMargin';
import CustomTooltip from '../../../components/common/tooltip';
import {
  convertToSnakeCase,
  getBorderProps,
  getcurrentDeviceType,
  getFontSize,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { ReduxSelectorProps } from '../../services/constants';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import '../text-editor/style.scss';
import WithAccordion from '../customComponents/CustomAccordion';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import QuilHelper from '../../puck/reusable-props/quilHelper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import { useForm, Controller } from 'react-hook-form';
import checkoutService from '../../api/checkoutService';
import { showNotification } from '../../api/constant';
import { useState } from 'react';
import { ICardFields } from '../../interface/formInterface';
import { CardFieldOption } from '../cart/CardFieldOption';
import { outlineProps } from '../../puck/reusable-props/Outline';

export type NewsLetterFieldProps = {
  border: IBorderValueProps;
  header: string;
  description: string;
  placeholderCustomize: ICardFields;
  note: string;
  background: {
    color: string;
    isInherit: boolean;
  };
  button: {
    text: string;
    color: string;
  };
} & MarginProps &
  PaddingProps &
  showComponentProps;

export const NewsLetter = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<NewsLetterFieldProps> => {
  return {
    label: (
      <CustomTooltip title="PhoneNumber">
        <span>News Letter</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      header: {
        ...QuillTextEditor('Header', true, 1).content,
      },
      description: {
        ...QuillTextEditor('Description', true, 2).content,
      },
      note: {
        ...QuillTextEditor('Note', true, 3).content,
      },
      placeholderCustomize: {
        type: 'custom',
        label: 'Place Holder',
        render: ({ value, onChange }) => {
          return (
            <CardFieldOption
              key="placeholder"
              name="placeholder"
              value={value}
              onChange={onChange}
            />
          );
        },
      },
      button: {
        type: 'custom',
        label: 'Button',
        render: ({ value, onChange }) => (
          <WithAccordion title="Button">
            <FieldLabel
              label={
                (
                  <CustomTooltip title="QuillEditor">
                    <span>Button Text</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }
              el="div">
              <QuilHelper
                value={value?.text}
                onChange={text => onChange({ ...value, text })}
                id={4}
              />
            </FieldLabel>
            <ColorPicker
              label="Color"
              tooltip="color"
              onChange={color => onChange({ ...value, color })}
              value={value?.color}
            />
          </WithAccordion>
        ),
      },
      ...backgroungFields,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
    },
    defaultProps: {
      placeholderCustomize: {
        placeholder: 'Enter Your Email Address',
        color: '#808080',
        fontSize: '14',
        fontWeight: '300',
        lineHeight: '1.5',
        textAlign: 'left',
        fontFamily: 'sans-serif',
        letterSpace: '0',
        textColor: {
          color: '#000000',
          isInherit: false,
        },
        textBackgroundColor: {
          color: '#ffffff',
          isInherit: false,
        },
        ...borderProps,
        ...outlineProps,
      },
      showComponent: ['desktop', 'tablet', 'mobile'],
      button: {
        text: '<p><span style="color: rgb(255, 255, 255); font-size: 14px;">SIGN UP</span></p>',
        color: '#000000',
      },
      ...marginProps,
      header:
        '<h1 class="ql-align-center"><span style="font-size: 32px;">Want First Dibs?</span></h1>',
      description:
        '<p class="ql-align-center"><span style="font-size: 16px;">Join our email list and be the first to know about new limited edition products, material innovations, and lots of other fun updates.</span></p>',
      note: '<p class="ql-align-center"><span style="font-size: 12px;">Note: You can opt-out at any time. See our Privacy Policy and Terms</span></p>',
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      ...borderProps,
    },
    render: ({
      margin,
      padding,
      editMode,
      border,
      background,
      header,
      placeholderCustomize,
      showComponent,
      description,
      button,
      note,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const [searchParams] = useSearchParams();
      const ShopID = searchParams.get('store');
      const [loading, setLoading] = useState(false);

      const methods = useForm({
        defaultValues: {
          email: '',
        },
      });
      const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
      } = methods;
      const onSubmit = async (data: { email: string }) => {
        try {
          setLoading(true);
          const response = await checkoutService.subscribeToNewsLetter({
            shopId: ShopID || '',
            email: data?.email,
          });
          if (response?.status === 200) {
            showNotification('success', response?.data?.Msg);
          }
        } catch (error) {
          showNotification('error', 'Subscription failed!');
          setLoading(false);
        }
        setLoading(false);
        reset();
      };
      const placeholderclass = convertToSnakeCase(
        placeholderCustomize?.placeholder,
      );

      const [isInputFocused, setIsInputFocused] = useState(false);
      return (
        <div
          className={`${isInputFocused ? placeholderCustomize?.outline?.borderStyle : placeholderCustomize?.border?.borderStyle} w-full h-full items-center text-editor-content ${showComponent?.includes(currentDeviceType) ? '' : '!hidden'}`}
          style={{
            ...marginStyles,
            ...paddingStyles,
            ...getBorderProps(border, currentDeviceType),
            backgroundColor: background?.isInherit
              ? 'inherit'
              : background?.color,
          }}>
          <div dangerouslySetInnerHTML={{ __html: header }} />
          <div
            className="pt-5"
            dangerouslySetInnerHTML={{ __html: description }}
          />

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="justify-center text-xs text-center pt-5 flex items-start">
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^\S+@\S+\.\S+$/,
                  message: 'Invalid email address',
                },
              }}
              render={({ field }) => (
                <div className="inline-block">
                  <input
                    style={{
                      ...getBorderProps(
                        isInputFocused
                          ? placeholderCustomize.outline
                          : placeholderCustomize.border,
                        currentDeviceType || 'desktop',
                      ),
                      color: placeholderCustomize?.textColor?.isInherit
                        ? 'inherit'
                        : placeholderCustomize?.textColor?.color,
                      backgroundColor: placeholderCustomize?.textBackgroundColor
                        ?.isInherit
                        ? 'inherit'
                        : placeholderCustomize?.textBackgroundColor?.color,
                    }}
                    {...field}
                    type="email"
                    className={`mr-3 p-2 border rounded custom-placeholder-${placeholderclass}`}
                    placeholder={placeholderCustomize?.placeholder}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                  />
                  <style>
                    {`
                       .custom-placeholder-${placeholderclass}::placeholder {
                         color: ${placeholderCustomize?.color};
                         font-size: ${getFontSize(placeholderCustomize?.fontSize)};
                         font-weight: ${placeholderCustomize?.fontWeight};
                         height: ${placeholderCustomize?.lineHeight};
                         text-align: ${placeholderCustomize?.textAlign};
                         font-family: ${placeholderCustomize?.fontFamily};
                         letter-spacing: ${placeholderCustomize?.letterSpace}px;
                       }
                     `}
                  </style>
                  {errors?.email && (
                    <div className="text-red-500 text-xs pt-1">
                      {errors?.email?.message}
                    </div>
                  )}
                </div>
              )}
            />

            <button
              disabled={loading}
              type="button"
              className="p-3"
              style={{
                backgroundColor: button?.color,
              }}
              onClick={() => {
                methods.handleSubmit(onSubmit)();
              }}>
              <div dangerouslySetInnerHTML={{ __html: button?.text }} />
            </button>
          </form>
          <div className="py-5" dangerouslySetInnerHTML={{ __html: note }} />
        </div>
      );
    },
  };
};
